/**
 * Public listing / entity-detail path helpers for Layout CLS shell reserve.
 * Keep sticky footer below the Lighthouse viewport while lazy routes resolve.
 *
 * Top AdSense Layout reserve was removed (empty 120px band above breadcrumbs).
 * Post-deploy Lighthouse spot-checks: /training-requirements/*, /restaurants,
 * /authors, /we-salute-you, /gallery, /instructors.
 */

const EXACT_LISTING_PATHS = new Set([
  '/events',
  '/events/',
  '/alerts',
  '/alerts/',
  '/for-officers',
  '/for-officers/',
  '/for-professionals',
  '/for-professionals/',
  '/write-for-apt',
  '/write-for-apt/',
  '/venues',
  '/venues/',
  '/agencies',
  '/agencies/',
  '/blog',
  '/blog/',
  '/blog/series',
  '/blog/series/',
  '/courses',
  '/courses/',
  '/courses/compare',
  '/courses/compare/',
  '/courses/requirements',
  '/courses/requirements/',
  '/instructors',
  '/instructors/',
  '/authors',
  '/authors/',
  '/restaurants',
  '/restaurants/',
  '/gallery',
  '/gallery/',
  '/we-salute-you',
  '/we-salute-you/',
  '/we-salute-you/submit',
  '/we-salute-you/submit/',
  '/professionals',
  '/professionals/',
  '/people',
  '/people/',
  '/people/unlist',
  '/people/unlist/',
  '/tac-shop',
  '/tac-shop/',
  '/secondary-employment',
  '/secondary-employment/',
  '/secondary-employment/for-employers',
  '/secondary-employment/for-employers/',
  // Marketing shells — reserve height, no top ad slot
  '/about',
  '/about/',
  '/standards/tics',
  '/standards/tics/',
  '/standards/tics/white-paper',
  '/standards/tics/white-paper/',
  '/standards/tics/specification',
  '/standards/tics/specification/',
  '/contact',
  '/contact/',
  '/certification',
  '/certification/',
  '/docs',
  '/docs/',
  '/support',
  '/support/',
]);

/**
 * Directory-style public indexes (breadcrumbs first — no top AdSense reserve slot).
 */
export function isPublicListingPath(pathname: string): boolean {
  if (EXACT_LISTING_PATHS.has(pathname)) {
    return true;
  }
  // Course taxonomy browse (same CoursesPage shell as /courses)
  if (/^\/courses\/domains(\/|$)/.test(pathname)) {
    return true;
  }
  // Tac-shop non-product public shells (cart/checkout/order)
  if (/^\/tac-shop\/(cart|checkout)\/?$/.test(pathname)) {
    return true;
  }
  if (/^\/tac-shop\/order\/[^/]+\/?$/.test(pathname)) {
    return true;
  }
  return false;
}

/**
 * Public entity detail + course hubs (reserve main min-height for footer CLS).
 */
const RESERVED_EVENT_SEGMENTS = new Set(['create']);
const RESERVED_AGENCY_SEGMENTS = new Set(['org-chart']);
const RESERVED_BLOG_SEGMENTS = new Set(['series', 'manage']);

export function isPublicEntityDetailPath(pathname: string): boolean {
  const eventsDetail = pathname.match(/^\/events\/([^/]+)\/?$/);
  if (eventsDetail && !RESERVED_EVENT_SEGMENTS.has(eventsDetail[1])) {
    return true;
  }
  if (/^\/venues\/[^/]+\/?$/.test(pathname)) {
    return true;
  }
  const agencyDetail = pathname.match(/^\/agencies\/([^/]+)\/?$/);
  if (agencyDetail && !RESERVED_AGENCY_SEGMENTS.has(agencyDetail[1])) {
    return true;
  }
  if (
    /^\/instructors\/[^/]+\/?$/.test(pathname) ||
    /^\/authors\/[^/]+\/?$/.test(pathname) ||
    /^\/restaurants\/[^/]+\/?$/.test(pathname) ||
    /^\/people\/unlist\/?$/.test(pathname) ||
    /^\/people\/[^/]+\/?$/.test(pathname) ||
    /^\/training-requirements\/[^/]+\/?$/.test(pathname) ||
    /^\/gallery\/photo\/[^/]+\/?$/.test(pathname) ||
    /^\/blog\/series\/[^/]+\/?$/.test(pathname) ||
    /^\/tac-shop\/product\/[^/]+\/?$/.test(pathname) ||
    /^\/secondary-employment\/listings\/[^/]+\/?$/.test(pathname) ||
    /^\/secondary-employment\/employers\/[^/]+\/?$/.test(pathname)
  ) {
    return true;
  }

  // Blog post detail — exclude series listing + manage console segment
  const blogPost = pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (blogPost && !RESERVED_BLOG_SEGMENTS.has(blogPost[1])) {
    return true;
  }

  // We Salute You detail — exclude submit
  const wsy = pathname.match(/^\/we-salute-you\/([^/]+)\/?$/);
  if (wsy && wsy[1] !== 'submit') {
    return true;
  }

  // Course hubs under a slug
  if (/^\/courses\/[^/]+\/(events|requirements|lesson-plans)\/?$/.test(pathname)) {
    return true;
  }

  // Course detail `/courses/:slug` — exclude listing reserved segments
  const courseDetail = pathname.match(/^\/courses\/([^/]+)\/?$/);
  if (courseDetail) {
    const slug = courseDetail[1];
    if (slug !== 'compare' && slug !== 'domains') {
      return true;
    }
  }

  // TCI short-link resolver (lands on course detail after redirect)
  if (/^\/c\/[^/]+\/?$/.test(pathname)) {
    return true;
  }

  return false;
}

/**
 * Listing indexes that manage their own loading min-height (avoid a permanent
 * ~2600px void above the footer once content renders).
 * `/alerts` is a short marketing landing — never apply the CLS shell reserve.
 */
const LISTING_PATHS_WITHOUT_SHELL_MIN_HEIGHT = new Set([
  '/agencies',
  '/agencies/',
  '/alerts',
  '/alerts/',
  '/for-officers',
  '/for-officers/',
  '/for-professionals',
  '/for-professionals/',
  '/write-for-apt',
  '/write-for-apt/',
]);

/** True when Layout should apply PUBLIC_SHELL_MIN_HEIGHT (home handled separately). */
export function shouldReservePublicShellMinHeight(pathname: string): boolean {
  if (LISTING_PATHS_WITHOUT_SHELL_MIN_HEIGHT.has(pathname)) {
    return false;
  }
  return isPublicEntityDetailPath(pathname) || isPublicListingPath(pathname);
}
